import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApiData } from "../lib/hooks";
import { TargetFaceLazy as TargetFace } from "./TargetFaceLazy";
import { toVbX, toVbY } from "../lib/targetGeometry";

// Target thumbnail with black arrow dots from drop-scored ends.
// r=10 viewbox units ≈ 3 px CSS at 72 px display — just visible as dots.
// Reads `shot_positions` ([x, y] pairs) straight off the enriched round summary
// (#1310) so the list no longer fetches per-round RoundDetail for dot positions.
function TargetThumb({ face, positions = [] }) {
  const dots = positions
    .filter(([x, y]) => x != null && y != null)
    .map(([x, y]) => ({ cx: toVbX(x), cy: toVbY(y) }));
  return (
    <div className="ip-thumb">
      <TargetFace face={face} size={72} />
      {dots.length > 0 && (
        <svg
          viewBox="0 0 240 240"
          className="ip-thumb-dots"
        >
          {dots.map((d, i) => (
            <circle key={i} cx={d.cx} cy={d.cy} r={10}
              fill="var(--nock)" stroke="white" strokeWidth={3} opacity={0.75} />
          ))}
        </svg>
      )}
    </div>
  );
}

// Home's in-progress panel shows only the most recent SHOWN_CAP rounds by
// default (a demo/heavy account can have dozens in flight at once, #QA
// 2026-09-13) — "Show all N" reveals the rest, "Show fewer" collapses back.
const SHOWN_CAP = 5;

/**
 * Shared in-progress rounds list used on both Home and New Round pages.
 *
 * Self-contained: fetches all active round summaries (which now carry
 * shot_positions for the dot scatter, #1310) and manages its own abandon
 * confirmation state — no per-round RoundDetail fetch.
 *
 * @param onCountChange  Called with the round count whenever it changes —
 *                       lets the parent update UI (e.g. greeting text).
 */
export function ActiveRoundsList({ onCountChange }) {
  const [abandoningId, setAbandoningId] = useState(null);
  const [error, setError]         = useState(null);
  const [expanded, setExpanded]   = useState(false);

  // Load-once through useApiData (#2068). The load error is not rendered:
  // the whole panel returns null when there are no rounds, so there is no
  // surface to put it on — `error` below stays the abandon-mutation error.
  // TargetThumb plots shot_positions, an opt-in list field (#2055), so this
  // is one of the four surfaces that must ask for it.
  const { data } = useApiData(
    () => api.rounds({ active: true, include: "thumbnails" }), [],
  );
  // abandon() drops a row optimistically without refetching, so the rendered
  // list is the fetched data minus whatever has been abandoned this session.
  const [abandoned, setAbandoned] = useState([]);
  const rounds = (data ?? []).filter((r) => !abandoned.includes(r.id));
  // Most-recent-first (started_at desc) — an unbounded flat list is what the
  // QA sweep flagged, not the ordering, but "most recent" is the useful
  // default slice once we cap it.
  const sortedRounds = [...rounds].sort(
    (a, b) => new Date(b.started_at) - new Date(a.started_at),
  );
  const visibleRounds = expanded ? sortedRounds : sortedRounds.slice(0, SHOWN_CAP);
  const hasMore = sortedRounds.length > SHOWN_CAP;

  useEffect(() => {
    onCountChange?.(rounds.length);
  }, [rounds.length, onCountChange]);

  async function abandon(roundId) {
    try {
      await api.abandonRound(roundId);
      setAbandoned((ids) => [...ids, roundId]);
      setAbandoningId(null);
    } catch (e) {
      setError(e.message);
    }
  }

  if (!rounds.length) return null;

  return (
    <div className="panel ip-panel">
      <div className="panel-head">
        <div className="panel-title">
          <span className="live-pip" />
          {rounds.length === 1 ? "Round in progress" : `${rounds.length} rounds in progress`}
        </div>
      </div>

      {error && (
        <div className="alc-error">
          {error}
        </div>
      )}

      {visibleRounds.map((round) => (
        <div key={round.id} className="ip-row">
          <TargetThumb face={round.target_face} positions={round.shot_positions || []} />

          <div className="ip-info">
            <div className="ip-name">{round.event_name}</div>
            <div className="ip-sub">
              {round.open_ended
                ? `${round.ends_done} ends so far`
                : `${round.ends_done} of ${round.ends_total} ends`}
              <span className="ip-sub-sep">·</span>
              Running total: {round.total}
            </div>
          </div>

          <div className={`ip-actions${abandoningId === round.id ? " ip-actions--confirm" : ""}`}>
            {abandoningId === round.id ? (
              <>
                <button className="btn ghost" onClick={() => setAbandoningId(null)}>Keep</button>
                <button className="btn danger" onClick={() => abandon(round.id)}>Confirm abandon</button>
              </>
            ) : (
              <button className="btn ghost" onClick={() => setAbandoningId(round.id)}>Abandon</button>
            )}
            <Link to={`/score?round=${round.id}`} className="btn primary">Resume</Link>
          </div>
        </div>
      ))}

      {hasMore && (
        <button
          type="button"
          className="btn ghost"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Show fewer" : `Show all ${sortedRounds.length}`}
        </button>
      )}
    </div>
  );
}
